import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ContactMessagesTable from "./ContactMessagesTable";
import ContactMessagesAccordion from "./ContactMessagesAccordion";
import api from "../../../services/api";
import { useSnackbarAlert } from "../../../hooks/useSnackbarAlert";

const AdminContactMessagesPage = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [expandedAccordionId, setExpandedAccordionId] = useState(null);

  const snackbar = useSnackbarAlert();

  const statusOptions = ["new", "read", "responded", "archived"];

  const [sortConfig, setSortConfig] = useState({
    field: "submissionDate",
    direction: "desc",
  });

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const res = await api.get("/contact-messages");
      setMessages(res.data);
    } catch (err) {
      console.error("Failed to load messages", err);
      snackbar.error("Failed to fetch messages");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  const handleDeleteMessage = async () => {
    if (!confirmDeleteId) return;
    try {
      await api.delete(`/contact-messages/${confirmDeleteId}`);
      setConfirmDeleteId(null);
      await fetchMessages();
      snackbar.success("Contact message deleted!");
    } catch (err) {
      snackbar.error("Failed to delete the message");
      console.error("Failed to delete message", err);
    }
  };

  const handleStatusChange = async (messageId, newStatus) => {
    const prevMessages = [...messages];
    const updatedMessages = messages.map((m) =>
      m._id === messageId ? { ...m, status: newStatus } : m
    );
    setMessages(updatedMessages);

    try {
      await api.put(`/contact-messages/${messageId}`, {
        status: newStatus,
      });
    } catch (err) {
      console.error("Failed to update status", err);
      setMessages(prevMessages);
      snackbar.error("Failed to update status");
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  const sortedMessages = [...messages].sort((a, b) => {
    const { field, direction } = sortConfig;
    const aVal = a[field];
    const bVal = b[field];

    if ((field === "submissionDate" || field === "createdAt") && aVal && bVal) {
      return direction === "asc"
        ? new Date(aVal) - new Date(bVal)
        : new Date(bVal) - new Date(aVal);
    }

    return direction === "asc"
      ? String(aVal || "").localeCompare(String(bVal || ""))
      : String(bVal || "").localeCompare(String(aVal || ""));
  });

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, mx: "auto" }}>
      <Typography variant="h5" fontWeight="bold" mb={3}>
        Contact Messages
      </Typography>

      {isMobile ? (
        <ContactMessagesAccordion
          messages={sortedMessages}
          onStatusUpdate={handleStatusChange}
          onRequestDelete={setConfirmDeleteId}
          statusOptions={statusOptions}
          expandedAccordionId={expandedAccordionId}
          setExpandedAccordionId={setExpandedAccordionId}
          sortConfig={sortConfig}
          setSortConfig={setSortConfig}
        />
      ) : (
        <ContactMessagesTable
          messages={sortedMessages}
          onStatusUpdate={handleStatusChange}
          onRequestDelete={setConfirmDeleteId}
          statusOptions={statusOptions}
          sortConfig={sortConfig}
          setSortConfig={setSortConfig}
        />
      )}

      <Dialog
        open={Boolean(confirmDeleteId)}
        onClose={() => setConfirmDeleteId(null)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this message? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteId(null)}>Cancel</Button>
          <Button
            onClick={handleDeleteMessage}
            color="error"
            variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminContactMessagesPage;
